import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadDocument, getDocumentsByFund, getDocument, approveDocument, getFunds } from '../api';
import { useTheme } from '../ThemeContext';
import { useMobile } from '../components/Layout';
import { Upload, X, CheckCircle, FileText, ChevronLeft } from 'lucide-react';

const STATUS_META = {
  PROCESSING:        { label: 'Processing',        bg: '#EFF6FF', text: '#1D4ED8' },
  COMPLIANCE_REVIEW: { label: 'Compliance Review', bg: '#FFFBEB', text: '#B45309' },
  APPROVED:          { label: 'Approved',           bg: '#F0FDF4', text: '#15803D' },
  SUPERSEDED:        { label: 'Superseded',         bg: null,      text: null      },
};

const SEVERITY_META = {
  HIGH:   { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
  MEDIUM: { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' },
  LOW:    { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
};

const Badge = ({ label, meta, theme }) => (
  <span style={{
    background:   meta?.bg   || theme.bgBadge,
    color:        meta?.text || theme.textSecondary,
    padding:      '2px 9px', borderRadius: 99,
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    border: `1px solid ${meta?.border || theme.border}`,
  }}>
    {label}
  </span>
);

const Documents = () => {
  const navigate      = useNavigate();
  const { theme }     = useTheme();
  const isMobile      = useMobile();

  const [funds,        setFunds]        = useState([]);
  const [selectedFund, setSelectedFund] = useState('');
  const [documents,    setDocuments]    = useState([]);
  const [selectedDoc,  setSelectedDoc]  = useState(null);
  const [docDetail,    setDocDetail]    = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [uploadMsg,    setUploadMsg]    = useState(null);
  const [approving,    setApproving]    = useState(false);
  const [activeTab,    setActiveTab]    = useState('sections');
  const [file,         setFile]         = useState(null);
  const [docType,      setDocType]      = useState('KID');
  const [showUpload,   setShowUpload]   = useState(false);

  useEffect(() => {
    getFunds().then(res => setFunds(res.data || []));
  }, []);

  useEffect(() => {
    if (!selectedFund) return;
    setLoading(true);
    setSelectedDoc(null);
    setDocDetail(null);
    getDocumentsByFund(selectedFund)
      .then(res => setDocuments(res.data.documents || []))
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  }, [selectedFund]);

  const loadDetail = async (doc) => {
    setSelectedDoc(doc);
    setDocDetail(null);
    setActiveTab('sections');
    try {
      const res = await getDocument(doc.id);
      setDocDetail(res.data);
    } catch (err) { console.error(err); }
  };

  const handleUpload = async () => {
    if (!file || !selectedFund) {
      setUploadMsg({ type: 'error', text: 'Please select a fund and a PDF file.' });
      return;
    }
    setUploading(true); setUploadMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fund_id', selectedFund);
      formData.append('document_type', docType);
      const res = await uploadDocument(formData);
      setUploadMsg({ type: 'success', text: `Processed. ${res.data.compliance?.high_flags || 0} HIGH flags, ${res.data.compliance?.medium_flags || 0} MEDIUM flags.` });
      setFile(null);
      const docsRes = await getDocumentsByFund(selectedFund);
      setDocuments(docsRes.data.documents || []);
      setShowUpload(false);
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.error || 'Upload failed.' });
    } finally { setUploading(false); }
  };

  const handleApprove = async () => {
    if (!selectedDoc) return;
    setApproving(true);
    try {
      await approveDocument(selectedDoc.id);
      const docsRes   = await getDocumentsByFund(selectedFund);
      const detailRes = await getDocument(selectedDoc.id);
      setDocuments(docsRes.data.documents || []);
      setDocDetail(detailRes.data);
      setSelectedDoc(prev => ({ ...prev, status: 'APPROVED' }));
    } catch (err) { console.error(err); }
    finally { setApproving(false); }
  };

  const flags       = docDetail?.kid_version?.compliance_flags?.flags || [];
  const highFlags   = flags.filter(f => f.severity === 'HIGH');
  const mediumFlags = flags.filter(f => f.severity === 'MEDIUM');

  const sections = docDetail?.kid_version ? [
    { key: 'section_1_what_is_product', label: '1. What is this product?' },
    { key: 'section_2_risk_reward',     label: '2. Risks & Returns' },
    { key: 'section_3_insolvency',      label: '3. Insolvency' },
    { key: 'section_4_costs',           label: '4. Costs' },
    { key: 'section_5_holding_period',  label: '5. Holding Period' },
    { key: 'section_6_complaints',      label: '6. Complaints' },
    { key: 'section_7_other',           label: '7. Other Information' },
  ] : [];

  const selectStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, fontSize: 13,
    background: theme.bgInput, color: theme.textPrimary,
    outline: 'none', cursor: 'pointer',
  };

  const ApproveBtn = ({ compact = false }) => (
    docDetail && selectedDoc?.status === 'COMPLIANCE_REVIEW' ? (
      <button onClick={handleApprove} disabled={approving} style={{
        padding: compact ? '6px 12px' : '7px 14px', borderRadius: 7,
        background: approving ? theme.bgBadge : '#3B82F6',
        color: approving ? theme.textTertiary : 'white',
        border: 'none', fontSize: 12, fontWeight: 700,
        cursor: approving ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <CheckCircle size={12} />
        {approving ? 'Approving…' : 'Approve'}
      </button>
    ) : null
  );

  const DetailContent = () => !selectedDoc ? null : (
    <>
      {!docDetail ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>Loading details…</div>
      ) : !docDetail.kid_version ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>No KID analysis available.</div>
      ) : (
        <>
          {/* Stats bar */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}` }}>
            {[
              { label: 'Risk Indicator', value: docDetail.kid_version.risk_indicator_1_7 ? `${docDetail.kid_version.risk_indicator_1_7}/7` : '—', color: theme.textPrimary },
              { label: 'HIGH Flags',     value: highFlags.length,   color: highFlags.length   > 0 ? '#DC2626' : '#15803D' },
              { label: 'MEDIUM Flags',   value: mediumFlags.length, color: mediumFlags.length > 0 ? '#B45309' : '#15803D' },
              { label: 'Status',         value: docDetail.kid_version.compliance_flags?.overall_status || '—', color: theme.textPrimary },
            ].map((s, i, arr) => (
              <div key={s.label} style={{ flex: 1, padding: '12px', textAlign: 'center', borderRight: i < arr.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</p>
                <p style={{ margin: '2px 0 0', fontSize: 9, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, overflowX: 'auto' }}>
            {[
              { key: 'sections',  label: '7 Sections' },
              { key: 'flags',     label: `Flags (${flags.length})` },
              { key: 'summaries', label: 'Summaries' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab.key ? '2px solid #3B82F6' : '2px solid transparent',
                fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? '#3B82F6' : theme.textSecondary,
                whiteSpace: 'nowrap', transition: 'color 0.15s',
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab body */}
          <div style={{ padding: '16px 18px', maxHeight: isMobile ? '40vh' : 500, overflowY: 'auto' }}>
            {activeTab === 'sections' && (
              <div>
                {sections.map(s => (
                  <div key={s.key} style={{ marginBottom: 16 }}>
                    <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</p>
                    <p style={{ margin: 0, fontSize: 12, color: theme.textSecondary, lineHeight: 1.7, background: theme.bgBase, padding: '10px 12px', borderRadius: 7, whiteSpace: 'pre-wrap', border: `1px solid ${theme.border}` }}>
                      {docDetail.kid_version[s.key] || '— Not found in document —'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'flags' && (
              <div>
                {flags.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#15803D', fontWeight: 600, fontSize: 13 }}>
                    <CheckCircle size={16} color="#15803D" /> No compliance issues detected.
                  </div>
                ) : (
                  <>
                    {docDetail.kid_version.compliance_flags?.summary && (
                      <div style={{ background: '#FFFBEB', borderRadius: 8, padding: '12px 14px', marginBottom: 14, border: '1px solid #FDE68A' }}>
                        <p style={{ margin: 0, fontSize: 12, color: '#B45309', lineHeight: 1.6 }}>{docDetail.kid_version.compliance_flags.summary}</p>
                      </div>
                    )}
                    {flags.map((flag, idx) => (
                      <div key={idx} style={{
                        marginBottom: 10, padding: '12px 14px', borderRadius: 8,
                        background: SEVERITY_META[flag.severity]?.bg || theme.bgBadge,
                        border: `1px solid ${SEVERITY_META[flag.severity]?.border || theme.border}`,
                      }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <Badge label={flag.severity} meta={SEVERITY_META[flag.severity]} theme={theme} />
                          <span style={{ fontSize: 11, color: theme.textTertiary }}>{flag.section}</span>
                        </div>
                        <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: theme.textPrimary }}>{flag.issue}</p>
                        <p style={{ margin: 0, fontSize: 11, color: theme.textSecondary, lineHeight: 1.5 }}>💡 {flag.recommendation}</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {activeTab === 'summaries' && (
              <div>
                {[
                  { label: '🇬🇧 English',  key: 'plain_language_summary_en' },
                  { label: '🇫🇷 Français', key: 'plain_language_summary_fr' },
                ].map(s => (
                  <div key={s.key} style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#3B82F6' }}>{s.label}</p>
                    <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7, background: theme.bgBase, padding: '14px 16px', borderRadius: 8, border: `1px solid ${theme.border}` }}>
                      {docDetail.kid_version[s.key] || '— Summary not available —'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>Document Processing</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
            PRIIPs KID extraction, compliance flagging and plain language summaries
          </p>
        </div>
        <button onClick={() => setShowUpload(v => !v)} style={{
          background: showUpload ? theme.bgBadge : '#3B82F6',
          color: showUpload ? theme.textSecondary : 'white',
          border: `1px solid ${showUpload ? theme.border : '#3B82F6'}`,
          borderRadius: 8, padding: '9px 16px', fontSize: 13,
          fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {showUpload ? <><X size={14} /> Close</> : <><Upload size={14} /> Upload</>}
        </button>
      </div>

      {/* Upload panel */}
      {showUpload && (
        <div style={{ background: theme.bgCard, borderRadius: 10, padding: '18px 20px', marginBottom: 16, border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 2fr auto', gap: 12, alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fund</label>
              <select value={selectedFund} onChange={e => setSelectedFund(e.target.value)} style={selectStyle}>
                <option value="">Select a fund…</option>
                {funds.map(f => <option key={f.id} value={f.id}>{f.fund_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Document Type</label>
              <select value={docType} onChange={e => setDocType(e.target.value)} style={selectStyle}>
                <option value="KID">KID</option>
                <option value="PROSPECTUS">Prospectus</option>
                <option value="SUPPLEMENT">Supplement</option>
                <option value="ANNUAL_REPORT">Annual Report</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>PDF File</label>
              <input type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13, color: theme.textPrimary, width: '100%' }} />
            </div>
            <button onClick={handleUpload} disabled={uploading || !file || !selectedFund} style={{
              padding: '9px 18px', borderRadius: 8,
              background: uploading || !file || !selectedFund ? theme.bgBadge : '#3B82F6',
              color: uploading || !file || !selectedFund ? theme.textTertiary : 'white',
              border: 'none', fontSize: 13, fontWeight: 700,
              cursor: uploading || !file || !selectedFund ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}>
              {uploading ? 'Processing…' : 'Upload & Analyse'}
            </button>
          </div>
          {uploadMsg && (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: uploadMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2', color: uploadMsg.type === 'success' ? '#15803D' : '#DC2626', border: `1px solid ${uploadMsg.type === 'success' ? '#BBF7D0' : '#FECACA'}` }}>
              {uploadMsg.text}
            </div>
          )}
        </div>
      )}

      {/* Fund selector */}
      {!showUpload && (
        <div style={{ background: theme.bgCard, borderRadius: 10, padding: '12px 16px', marginBottom: 16, border: `1px solid ${theme.border}`, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>Fund:</label>
          <select value={selectedFund} onChange={e => setSelectedFund(e.target.value)} style={{ ...selectStyle, flex: 1, maxWidth: 300 }}>
            <option value="">Select a fund…</option>
            {funds.map(f => <option key={f.id} value={f.id}>{f.fund_name}</option>)}
          </select>
        </div>
      )}

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: !isMobile && selectedDoc ? '320px 1fr' : '1fr', gap: 16, alignItems: 'start' }}>

        {/* Document list */}
        <div>
          {!selectedFund ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 13, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
              <FileText size={28} color={theme.textTertiary} style={{ marginBottom: 10 }} />
              <p style={{ margin: 0 }}>Select a fund to view its documents.</p>
            </div>
          ) : loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>Loading…</div>
          ) : documents.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 13, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
              No documents uploaded yet.
            </div>
          ) : documents.map(doc => {
            const isSelected = selectedDoc?.id === doc.id;
            return (
              <div key={doc.id} onClick={() => loadDetail(doc)} style={{
                background: theme.bgCard, borderRadius: 10, padding: '14px 16px', marginBottom: 8,
                border: isSelected ? '2px solid #3B82F6' : `1px solid ${theme.border}`,
                boxShadow: isSelected ? '0 0 0 3px rgba(59,130,246,0.1)' : theme.shadowSm,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#93C5FD'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = theme.border; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: 13, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.filename}
                    </p>
                    <p style={{ margin: '0 0 8px', fontSize: 11, color: theme.textTertiary }}>
                      v{doc.version_number} · {doc.page_count} pages · {doc.document_type}
                    </p>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <Badge label={STATUS_META[doc.status]?.label || doc.status} meta={STATUS_META[doc.status]} theme={theme} />
                      {doc.high_flag_count   > 0 && <Badge label={`${doc.high_flag_count} HIGH`}   meta={SEVERITY_META.HIGH}   theme={theme} />}
                      {doc.medium_flag_count > 0 && <Badge label={`${doc.medium_flag_count} MED`}  meta={SEVERITY_META.MEDIUM} theme={theme} />}
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary, flexShrink: 0 }}>
                    {new Date(doc.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop detail panel */}
        {!isMobile && selectedDoc && (
          <div style={{ background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm, overflow: 'hidden', position: 'sticky', top: 90 }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: theme.textPrimary }}>{selectedDoc.filename}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>
                  {docDetail?.kid_version ? `KID Version ${docDetail.kid_version.version_number}` : 'Processing…'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ApproveBtn />
                <button onClick={() => { setSelectedDoc(null); setDocDetail(null); }} style={{ background: theme.bgBadge, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 8, display: 'flex', color: theme.textSecondary }}>
                  <X size={14} />
                </button>
              </div>
            </div>
            <DetailContent />
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {isMobile && selectedDoc && (
        <>
          <div onClick={() => { setSelectedDoc(null); setDocDetail(null); }} style={{ position: 'fixed', inset: 0, background: theme.bgOverlay, zIndex: 200 }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: theme.bgCard, borderRadius: '16px 16px 0 0', zIndex: 201, maxHeight: '88vh', overflowY: 'auto', boxShadow: theme.shadowLg }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: theme.bgCard, zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedDoc.filename}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>{docDetail?.kid_version ? `KID v${docDetail.kid_version.version_number}` : 'Processing…'}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <ApproveBtn compact />
                <button onClick={() => { setSelectedDoc(null); setDocDetail(null); }} style={{ background: theme.bgBadge, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 8, display: 'flex', color: theme.textSecondary }}>
                  <X size={14} />
                </button>
              </div>
            </div>
            <DetailContent />
          </div>
        </>
      )}
    </div>
  );
};

export default Documents;